import {makeStyles} from "@material-ui/core"
import {ModernTheme} from "../../../../../theme/modern/tokens"
import {COARSE, touchField} from "../../../../../theme/modern/touchTargets"

/**
 * Ported from the prototype's `pages/Login/login.css` — the `.login-card`,
 * `.field`, `.input`, `.login-error` and `.login-submit` rule sets.
 *
 * Every colour comes from `theme.modern`, so reading-room (dark) mode is
 * handled by the token swap in ModernThemeProvider and this file never needs a
 * `html[data-theme="dark"]` selector.
 */
export default makeStyles((theme: ModernTheme) => ({
    card: {
        width: "100%",
        maxWidth: 380,
        background: theme.modern.panel,
        border: `1px solid ${theme.modern.line}`,
        borderRadius: 16,
        boxShadow: theme.modern.shadow,
        padding: "30px 30px 26px",

        "@media (max-width:420px)": {padding: "24px 20px 22px"},
    },
    title: {
        font: `700 22px/1.2 ${theme.modern.sans}`,
        letterSpacing: "-.015em",
        color: theme.modern.ink,
        margin: 0,
    },
    sub: {
        marginTop: 5,
        marginBottom: 0,
        font: `400 13px ${theme.modern.sans}`,
        color: theme.modern.ink2,
    },

    // ---- fields ----
    field: {
        display: "block",
        marginTop: 16,
    },
    field_label: {
        display: "block",
        font: `600 11px/1 ${theme.modern.sans}`,
        letterSpacing: ".04em",
        color: theme.modern.ink2,
        marginBottom: 6,
        textTransform: "uppercase",
    },
    input: {
        width: "100%",
        height: 42,
        border: `1px solid ${theme.modern.line}`,
        borderRadius: 9,
        background: theme.modern.panel2,
        color: theme.modern.ink,
        font: `400 14px ${theme.modern.sans}`,
        padding: "0 12px",
        transition: "border-color .12s, box-shadow .12s",
        // iOS Safari otherwise paints its own inset shadow and rounded corners
        WebkitAppearance: "none",

        "&::placeholder": {color: theme.modern.ink3},

        "&:focus": {
            outline: "none",
            borderColor: theme.modern.accent,
            boxShadow: `0 0 0 3px ${theme.modern.accentSoft}`,
        },

        // 14 px was under the 16 px iOS threshold, so signing in on an iPad
        // zoomed the card on the first tap into the login field — the first
        // impression the app makes on a tablet. WebkitAppearance above already
        // handles the other half of Safari's input styling.
        [COARSE]: touchField(),
    },
    input__error: {
        borderColor: theme.modern.crit,

        "&:focus": {
            borderColor: theme.modern.crit,
            boxShadow: `0 0 0 3px ${theme.modern.critSoft}`,
        },
    },
    field_error: {
        display: "block",
        marginTop: 5,
        font: `500 11.5px ${theme.modern.sans}`,
        color: theme.modern.crit,
    },

    // ---- submit / error ----
    sign_in_error: {
        marginTop: 14,
        padding: "9px 12px",
        borderRadius: 9,
        background: theme.modern.critSoft,
        color: theme.modern.crit,
        font: `500 12.5px ${theme.modern.sans}`,
        // the prototype uses color-mix(in srgb, var(--crit) 24%, transparent)
        // here; CRA 3.3's browser matrix predates color-mix, and no 24%-crit
        // translucent exists as a token, so the solid crit line stands in
        border: `1px solid ${theme.modern.crit}`,
    },
    submit_button: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        width: "100%",
        height: 44,
        marginTop: 18,
        borderRadius: 10,
        border: `1px solid ${theme.modern.accent}`,
        background: theme.modern.accent,
        color: theme.modern.onAccent,
        font: `600 14px ${theme.modern.sans}`,
        // was `capitalize`, which existed only to hide the lowercase
        // `sign_in.submit` string; the global button rule now owns the casing
        textTransform: "uppercase",
        cursor: "pointer",
        transition: "filter .12s",

        "&:hover:not(:disabled)": {filter: "brightness(1.07)"},
        "&:disabled": {opacity: 0.7, cursor: "default"},
    },
    submit_spinner: {
        flex: "none",
        color: theme.modern.onAccent,
    },
}))
